import {
  ArrowLongRightIcon,
  ArrowTurnDownRightIcon,
  ArrowTurnUpRightIcon,
  BookOpenIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import { useState } from "react";
import toast from "react-hot-toast";
import Skeleton from "react-loading-skeleton";
import { Link, useNavigate } from "react-router-dom";
import { policyDocumentPath } from "../Policies/api";
import { useAuth } from "../../context/AuthContext";
import { StatuteType } from "../../db/schema";
import Button from "../common/Button";
import Tooltip from "../common/Tooltip";
import { toastOptions } from "../common/toastOptions";
import cn from "classnames";

interface StatutesProps {
  caseStatutes?: StatuteType[];
  onInsertContent?: (content: string, mode?: string) => void;
  caseId?: string;
  onUpdateStatutes?: (statutes: StatuteType[]) => void;
  setCaseStatutes?: React.Dispatch<React.SetStateAction<StatuteType[]>>;
  editorContent?: string;
  isCaseLoading?: boolean;
}

function Statutes({
  caseStatutes = [],
  onInsertContent,
  caseId,
  onUpdateStatutes,
  setCaseStatutes,
  editorContent,
  isCaseLoading,
}: StatutesProps) {
  const [isIdentifying, setIsIdentifying] = useState(false);
  const { reportPreferences, checkAuthStatus } = useAuth();

  const StatutesSkeleton = () => (
    <div className="p-4 bg-base-200 rounded-lg">
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <Skeleton width={80} height={20} />
          <Skeleton width={60} height={20} className="rounded-full" />
        </div>
        <Skeleton width={200} height={16} />
        <Skeleton count={3} height={16} />
      </div>
    </div>
  );

  const formatStatuteContent = (statute: StatuteType) => {
    return `${statute.description} (${statute.code} - ${statute.name})`;
  };

  const handleInsertContent = (statute: StatuteType, mode?: string) => {
    if (onInsertContent) {
      onInsertContent(formatStatuteContent(statute), mode);
      toast.success("Added to report");
    }
  };

  const navigate = useNavigate();
  // Statutes taken from the agency's own uploaded codes open in the in-app
  // viewer at their page; everything else goes to a web search.
  const handleSearchStatute = (statute: StatuteType) => {
    if (statute.documentId) {
      navigate(policyDocumentPath(statute.documentId, { page: statute.page, sectionId: statute.sectionId }));
      return;
    }
    const searchQuery = encodeURIComponent(
      `${statute.code} ${statute.name} law statute`
    );
    window.open(`https://www.google.com/search?q=${searchQuery}`, "_blank");
  };

  const getLikelihoodColor = (likelihood: number) => {
    if (likelihood >= 90) return "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400";
    if (likelihood >= 70) return "bg-yellow-100 text-yellow-800 dark:bg-yellow-950/40 dark:text-amber-400";
    return "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400";
  };

  const getLikelihoodText = (likelihood: number) => {
    if (likelihood >= 90) return "Strong Match";
    if (likelihood >= 70) return "Match";
    return "Weak Match";
  };

  const handleIdentifyStatutes = async () => {
    if (!caseId) return;

    const toastId = toast.loading("Identifying relevant statutes...", {
      duration: Infinity,
    });

    try {
      setIsIdentifying(true);
      const response = await axios.post("/api/statutes", {
        context: editorContent,
        reportPreferences,
        caseId,
      });
      const newStatutes = response.data.statutes;

      // Update both local state and database
      if (setCaseStatutes) {
        setCaseStatutes(newStatutes);
      }
      if (onUpdateStatutes) {
        onUpdateStatutes(newStatutes);
      }

      toast.success("Successfully identified relevant statutes", {
        id: toastId,
        ...toastOptions,
      });
    } catch (error) {
      checkAuthStatus();
      console.error("Error identifying statutes:", error);
      toast.error("Failed to identify statutes", {
        id: toastId,
      });
    } finally {
      setIsIdentifying(false);
    }
  };

  return (
    <div className="pt-2 md:pt-0" data-tour="statutes-panel">
      <div className={cn(isIdentifying && "opacity-35 pointer-events-none")}>
        <div className="flex items-center prose mb-2">
          <BookOpenIcon className="size-5 text-blue-500 dark:text-blue-300" />
          <h3 className="m-0 ml-2 text-base">Statutes</h3>
        </div>
        <div className="grid gap-y-2">
          {isCaseLoading ? (
            Array.from({ length: 6 }, (_, index) => (
              <StatutesSkeleton key={index} />
            ))
          ) : caseStatutes && caseStatutes.length > 0 ? (
            <>
              <div className="bg-blue-50 dark:bg-blue-900/40 p-4 rounded-lg mb-2">
                <p className="text-xs p-0 m-0">
                  Statutes are identified automatically and may contain
                  errors or inaccuracies. It is the responsibility of the
                  reviewing officer to verify all statutes before including them
                  in official reports.
                </p>
              </div>
              {caseStatutes
                .sort((a, b) => b.likelihood - a.likelihood)
                .map((statute, index) => (
                <Tooltip
                  key={`${statute.code}-${index}`}
                  placement="top"
                  overlay={statute.source}
                >
                  <div className="dropdown dropdown-end" data-tour="statute-item">
                    <div
                      tabIndex={0}
                      className="p-4 text-sm cursor-pointer transition duration-100 border-b-2 bg-base-200 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/40 border-transparent"
                    >
                      <div className="flex justify-between items-start mb-1 gap-2">
                        <h4 className="text-xs px-2 py-1 font-semibold max-w-[50%] bg-blue-50 dark:bg-blue-900/40 rounded-md flex items-center gap-1.5">
                          <span className="truncate">{statute.code}</span>
                          {statute.documentId && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500 text-white font-medium" title="From your department's documents">
                              Dept
                            </span>
                          )}
                        </h4>
                        <span
                          className={`text-xs text-center px-2 py-1 rounded-full max-w-[50%] ${getLikelihoodColor(
                            statute.likelihood
                          )}`}
                        >
                          {getLikelihoodText(statute.likelihood)}
                        </span>
                      </div>
                      <h5 className="font-medium text-xs mb-1">
                        <span className="text-blue-500 dark:text-blue-300">{statute.grade}</span> -{" "}
                        {statute.name}
                      </h5>
                      <p className="text-gray-600 dark:text-base-content/70 line-clamp-5">
                        {statute.description.endsWith(".")
                          ? statute.description
                          : `${statute.description}.`}
                      </p>
                    </div>
                    <ul
                      tabIndex={0}
                      className="dropdown-content menu bg-base-100 rounded-lg z-20 p-2 shadow mt-2 w-full"
                    >
                      <li>
                        <p
                          className="hover:bg-gray-100 dark:hover:bg-base-200"
                          onClick={() => handleInsertContent(statute, "cursor")}
                        >
                          <ArrowLongRightIcon className="size-4" />
                          Add to cursor
                        </p>
                      </li>
                      <li>
                        <p
                          className="hover:bg-gray-100 dark:hover:bg-base-200"
                          onClick={() => handleInsertContent(statute, "top")}
                        >
                          <ArrowTurnUpRightIcon className="size-4" />
                          Add to top of report
                        </p>
                      </li>
                      <li>
                        <p
                          className="hover:bg-gray-100 dark:hover:bg-base-200"
                          onClick={() => handleInsertContent(statute, "end")}
                        >
                          <ArrowTurnDownRightIcon className="size-4" />
                          Add to end of report
                        </p>
                      </li>
                      <li>
                        <p
                          className="hover:bg-gray-100 dark:hover:bg-base-200"
                          onClick={() => handleSearchStatute(statute)}
                        >
                          <MagnifyingGlassIcon className="size-4" />
                          {statute.documentId ? "Open section" : "More Info"}
                        </p>
                      </li>
                    </ul>
                  </div>
                </Tooltip>
              ))}
            </>
          ) : (
            <p className="text-sm my-2">
              Automatically identify and insert relevant legal codes to
              strengthen your report. You must set your state under "Include
              Statutes" in the Report Preferences to utilize this&nbsp;feature.
            </p>
          )}
          {reportPreferences?.statute_state ? (
            <Button
              variant="primary"
              className="w-full mb-4 md:mb-0"
              data-tour="statute-identify"
              onClick={handleIdentifyStatutes}
              loading={isIdentifying}
              disabled={isIdentifying || !caseId}
            >
              Identify Statutes
            </Button>
          ) : (
            <Link to="/preferences?highlight=statutes">
              <Button
                variant="primary"
                className="w-full mb-4 md:mb-0"
                disabled={isIdentifying || !caseId}
              >
                Set Statutes State
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default Statutes;
